import { Button, Icons, Stack, Typography } from '@healthinal/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import {
  getGetScreeningCaseByIdQueryOptions,
  getGetScreeningTracingIndividualQueryKey,
  getScreeningTracingIndividual,
  ScreeningCaseDto,
  useGetScreeningCaseByIdSuspense,
} from '../../api/created/screening.ts';
import { JournalDtoTopic } from '../../api/generated/model';
import { queryClient } from '../../api/queryClient.ts';
import { Breadcrumbs } from '../../components/Breadcrumbs.tsx';
import { ContentSection } from '../../components/ContentSection.tsx';
import { ErrorBoundary } from '../../components/errors/ErrorBoundary.tsx';
import { MotionDivider } from '../../components/MotionDivider.tsx';
import { StickyContainer } from '../../components/StickyContainer.tsx';
import { TruncateText } from '../../components/TruncateText.tsx';
import { SCREENING_DOCUMENT_CATEGORIES } from '../../utils/document.ts';
import { getScreeningAssessmentResultFromStatus } from '../../utils/screening.ts';
import { CreateJournalEntryDialog } from '../components/journal/CreateJournalEntryDialog.tsx';
import { CreateDocumentDialog } from '../patients/components/CreateDocumentDialog.tsx';
import { UploadDocumentDialog } from '../patients/components/UploadDocumentDialog.tsx';
import { ScreeningIndividualSideNavigation } from './$screeningId/components/ScreeningIndividualSideNavigation.tsx';
import { ScreeningSideNavigation } from './$screeningId/components/ScreeningSideNavigation.tsx';
import { SetScreeningAssessmentResultDialog } from './$screeningId/components/SetScreeningAssessmentResultDialog.tsx';
import { ScreeningTracingContactDialog } from './components/ScreeningTracingContactDialog.tsx';

export const Route = createFileRoute('/screening/$screeningId')({
  component: ScreeningLayout,
  loader: async ({ params: { screeningId } }) =>
    await queryClient.ensureQueryData(getGetScreeningCaseByIdQueryOptions(screeningId)),
  staticData: ({ loaderData: screening }: { loaderData: ScreeningCaseDto }) => {
    return { title: screening.screeningAssignment };
  },
});

function ScreeningLayout() {
  const { screeningId } = Route.useParams();
  const { data: screening } = useGetScreeningCaseByIdSuspense(screeningId);
  const contentSectionRef = useRef(null);
  const [isCreateContactOpen, setIsCreateContactOpen] = useState(false);
  const [isSetStatusDialogOpen, setIsSetStatusDialogOpen] = useState(false);
  const [isJournalDialogOpen, setIsJournalDialogOpen] = useState(false);
  const [isCreateDocumentDialogOpen, setIsCreateDocumentDialogOpen] = useState(false);
  const [isUploadDocumentDialogOpen, setIsUploadDocumentDialogOpen] = useState(false);
  const matches = useMatches();
  const currentMatch = matches[matches.length - 1];
  const individualId = (currentMatch.params as Record<string, string | undefined>).individualId;
  const isIndividualPersonalDetailsRoute =
    currentMatch.routeId === '/screening/$screeningId/individuals/$individualId/';
  const isIndividualDocumentRoute =
    currentMatch.routeId === '/screening/$screeningId/individuals/$individualId/document';

  const { data: individual } = useQuery({
    queryKey: getGetScreeningTracingIndividualQueryKey(individualId!),
    queryFn: ({ signal }) => getScreeningTracingIndividual(individualId!, undefined, signal),
    enabled: !!individualId,
  });

  return (
    <>
      <Stack direction="row" gap={6}>
        {individualId ? (
          <ScreeningIndividualSideNavigation screeningId={screeningId} individualId={individualId} />
        ) : (
          <ScreeningSideNavigation screeningId={screeningId} />
        )}

        <ContentSection ref={contentSectionRef} flex={1} gap={2}>
          <StickyContainer>
            <Stack direction="row" justifyContent="space-between" alignItems="start" gap={2}>
              <Stack minWidth={0} flex={1}>
                <Typography level="h2">
                  <TruncateText maxWidth="100%">{screening.screeningAssignment}</TruncateText>
                </Typography>
                <Breadcrumbs />
              </Stack>

              {currentMatch.routeId === '/screening/$screeningId/individuals/' && (
                <Button
                  endDecorator={<Icons.Add />}
                  onClick={() => setIsCreateContactOpen(true)}
                  sx={{ flexShrink: 0 }}>
                  Neue Screening-Person
                </Button>
              )}
              {isIndividualPersonalDetailsRoute && (
                <Stack direction="row" gap={1} sx={{ flexShrink: 0 }}>
                  <Button color="neutral" onClick={() => setIsJournalDialogOpen(true)} endDecorator={<Icons.Notes />}>
                    Notiz erfassen
                  </Button>
                  <Button endDecorator={<Icons.Flaky />} onClick={() => setIsSetStatusDialogOpen(true)}>
                    Abschluss
                  </Button>
                </Stack>
              )}
              {isIndividualDocumentRoute && (
                <Stack direction="row" gap={2} sx={{ flexShrink: 0 }}>
                  <Button
                    color="neutral"
                    endDecorator={<Icons.UploadFile />}
                    onClick={() => setIsUploadDocumentDialogOpen(true)}>
                    Dokument hochladen
                  </Button>
                  <Button
                    color="neutral"
                    endDecorator={<Icons.Add />}
                    onClick={() => setIsCreateDocumentDialogOpen(true)}>
                    Dokument erstellen
                  </Button>
                </Stack>
              )}
            </Stack>

            <MotionDivider contentSectionRef={contentSectionRef} />
          </StickyContainer>

          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </ContentSection>
      </Stack>
      {individualId && (
        <>
          <SetScreeningAssessmentResultDialog
            screeningId={individualId}
            open={isSetStatusDialogOpen}
            onClose={() => setIsSetStatusDialogOpen(false)}
            initialResult={
              individual?.assessmentStatus ? getScreeningAssessmentResultFromStatus(individual.assessmentStatus) : null
            }
            initialNotes={individual?.assessmentNotes}
          />
          <CreateJournalEntryDialog
            patientId={individualId}
            preselectedTopic={JournalDtoTopic.SCREENING_PERSON}
            open={isJournalDialogOpen}
            onClose={() => setIsJournalDialogOpen(false)}
            disabledTopics={[
              JournalDtoTopic.NONE,
              JournalDtoTopic.ANAMNESIS,
              JournalDtoTopic.THERAPY,
              JournalDtoTopic.DIAGNOSTICS,
              JournalDtoTopic.PERSONAL_DETAILS,
              JournalDtoTopic.NETWORK,
              JournalDtoTopic.TRACING_CONTACT,
              JournalDtoTopic.CONTACT_TRACING,
            ]}
          />
          <CreateDocumentDialog
            patientId={individualId}
            categories={SCREENING_DOCUMENT_CATEGORIES}
            open={isCreateDocumentDialogOpen}
            onClose={() => setIsCreateDocumentDialogOpen(false)}
          />
          <UploadDocumentDialog
            patientId={individualId}
            categories={SCREENING_DOCUMENT_CATEGORIES}
            open={isUploadDocumentDialogOpen}
            onClose={() => setIsUploadDocumentDialogOpen(false)}
          />
        </>
      )}
      <ScreeningTracingContactDialog
        screeningCaseId={screening.id}
        open={isCreateContactOpen}
        onClose={() => setIsCreateContactOpen(false)}
      />
    </>
  );
}
